import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";
import { environment } from "../../environments/environment";

type GtagCommand = "config" | "event" | "js";

type GoogleTagState = {
  enabled: boolean;
  hasDataLayer: boolean;
  hasGtag: boolean;
  measurementId?: string;
  scriptSources: string[];
};

type PageViewEvent = {
  event: "page_view";
  pageLocation: string;
  pagePath: string;
  pageTitle: string;
  routeName: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (command: GtagCommand, targetIdOrDate: string | Date, config?: Record<string, unknown>) => void;
  }
}

@Injectable({ providedIn: "root" })
export class AnalyticsService {
  private initialized = false;
  private readonly enabled = environment.analytics?.enabled ?? true;
  private readonly measurementId = environment.analytics?.ga4MeasurementId;
  private readonly debugEnabled = environment.analytics?.debug ?? false;
  private readonly debugMode = environment.analytics?.debugMode ?? false;

  constructor(private router: Router) {}

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    if (!this.enabled || !this.measurementId) {
      this.log("GA4 tracking skipped", {
        enabled: this.enabled,
        tagState: this.getGoogleTagState(),
      });
      return;
    }

    this.initializeGoogleTag();

    this.log("GA4 tracking initialized", {
      tagState: this.getGoogleTagState(),
    });

    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      this.trackPageView(event.urlAfterRedirects);
    });
  }

  private initializeGoogleTag(): void {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag(...args: unknown[]) {
        window.dataLayer?.push(args);
      };

    if (!this.hasGoogleTagScript()) {
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
      document.head.appendChild(script);
    }

    window.gtag("js", new Date());
    window.gtag("config", this.measurementId, {
      debug_mode: this.debugMode,
      send_page_view: false,
    });
  }

  private trackPageView(url: string): void {
    if (!this.measurementId || !window.gtag) {
      return;
    }

    const pageViewEvent = this.buildPageViewEvent(url);

    window.gtag("event", "page_view", {
      page_location: pageViewEvent.pageLocation,
      page_path: pageViewEvent.pagePath,
      page_title: pageViewEvent.pageTitle,
      route_name: pageViewEvent.routeName,
      debug_mode: this.debugMode,
      send_to: this.measurementId,
    });

    this.log("Sent GA4 page_view", {
      ...pageViewEvent,
      tagState: this.getGoogleTagState(),
    });
  }

  private buildPageViewEvent(url: string): PageViewEvent {
    const pagePath = url.split("?")[0] || "/";

    return {
      event: "page_view",
      pageLocation: this.getPageLocation(url),
      pagePath,
      pageTitle: document.title,
      routeName: this.getRouteName(pagePath),
    };
  }

  private getPageLocation(url: string): string {
    if (url.startsWith("http")) {
      return url;
    }

    return `${window.location.origin}${url}`;
  }

  private getRouteName(pagePath: string): string {
    if (pagePath.includes("box-style")) {
      return "Box Style";
    }

    if (pagePath.includes("box-options")) {
      return "Box Options";
    }

    if (pagePath.includes("design-services")) {
      return "Design Services";
    }

    if (pagePath.includes("design")) {
      return "Designer";
    }

    if (pagePath.includes("review-box")) {
      return "Review Box";
    }

    if (pagePath.includes("upload-design")) {
      return "Upload Design";
    }

    if (pagePath.includes("box-edit")) {
      return "Edit Order";
    }

    return "FedEx Custom Boxes";
  }

  private hasGoogleTagScript(): boolean {
    return Array.from(document.scripts).some((script) => script.src.includes("googletagmanager.com/gtag/js"));
  }

  private getGoogleTagState(): GoogleTagState {
    return {
      enabled: this.enabled,
      hasDataLayer: Array.isArray(window.dataLayer),
      hasGtag: typeof window.gtag === "function",
      measurementId: this.measurementId,
      scriptSources: Array.from(document.scripts)
        .map((script) => script.src)
        .filter((src) => src.includes("googletagmanager.com") || src.includes("google-analytics.com")),
    };
  }

  private log(message: string, data: Record<string, unknown>): void {
    if (this.debugEnabled) {
      console.log(`[FedEx Analytics] ${message}`, data);
    }
  }
}
